import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, writeBatch } from "firebase/firestore";
import { db } from "./firebase";
import { levelToFirestore } from "../models/level";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * LevelService
 * Centraliza as operações de banco de dados para o módulo de Níveis.
 * Coleção: tenants/{idTenant}/levels
 */
const LevelService = {
    /**
     * Busca todos os níveis de um tenant.
     */
    getLevels: async (idTenant) => {
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.LEVELS),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const levels = [];
            querySnapshot.forEach((docSnap) => {
                levels.push({ id: docSnap.id, ...docSnap.data() });
            });
            return levels;
        } catch (error) {
            console.error("Erro ao listar níveis:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza um nível.
     */
    save: async (formData, idTenant, authUser, existingLevels = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const levelData = levelToFirestore(formData, idTenant, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const levelRef = doc(db, "tenants", idTenant, "levels", formData.id);
                const oldLevel = existingLevels.find(l => l.id === formData.id) || {};
                await updateDoc(levelRef, levelData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldLevel, levelData);
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.LEVELS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: levelData.name,
                    description: `Atualizou o nível "${levelData.name}"`,
                    details: changes
                });

                return { id: formData.id, ...levelData };
            } else {
                // CREATE — define order como último
                if (!formData.order && existingLevels.length > 0) {
                    levelData.order = existingLevels.length;
                }

                const newDocRef = await addDoc(collection(db, "tenants", idTenant, COLLECTIONS.LEVELS), levelData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.LEVELS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: levelData.name,
                    description: `Criou o nível "${levelData.name}"`
                });

                return { id: newDocRef.id, ...levelData };
            }
        } catch (error) {
            console.error("Erro no LevelService.save:", error);
            throw error;
        }
    },

    /**
     * Remove um nível.
     */
    delete: async (levelId, idTenant, authUser, levelName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, "levels", levelId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.LEVELS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: levelId,
                entityName: levelName,
                description: `Excluiu o nível "${levelName || levelId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no LevelService.delete:", error);
            throw error;
        }
    },

    /**
     * Reordena a lista de níveis em massa.
     */
    reorder: async (items, idTenant, authUser) => {
        if (!idTenant || !items.length) return;

        try {
            const batch = writeBatch(db);
            items.forEach((item, index) => {
                const docRef = doc(db, "tenants", idTenant, "levels", item.id);
                batch.update(docRef, { order: index });
            });

            await batch.commit();

            // Auditoria simplificada do lote
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.LEVELS,
                action: AUDIT_ACTIONS.UPDATE,
                description: `Reordenou a lista de níveis (${items.length} itens)`
            });

            return true;
        } catch (error) {
            console.error("Erro no LevelService.reorder:", error);
            throw error;
        }
    }
};

export default LevelService;
